import React, { useState, useEffect, useRef } from 'react';
import ToggleFundingButton from './ToggleFundingButton';

interface MapContainerProps {
  mapRef: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
  showFunding?: boolean;
  setShowFunding?: (show: boolean) => void;
}

const MapContainer: React.FC<MapContainerProps> = ({ 
  mapRef, 
  children, 
  showFunding = true,
  setShowFunding = () => {}
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const initializedRef = useRef(false);
  
  // Sync body class with showFunding prop on mount
  useEffect(() => {
    console.log("MapContainer: initializing with showFunding =", showFunding);
    
    // Only apply the initial class once
    if (!initializedRef.current) {
      if (!showFunding) {
        document.body.classList.add('hide-funding');
        console.log("MapContainer: Initial state - added hide-funding class");
      } else {
        document.body.classList.remove('hide-funding');
        console.log("MapContainer: Initial state - removed hide-funding class");
      }
      initializedRef.current = true;
    }
    
    // Keep track of when the funding state changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class' && 
            mutation.target === document.body) {
          const hasFundingHidden = document.body.classList.contains('hide-funding');
          console.log("MapContainer: Body class changed, hide-funding is now", hasFundingHidden);
        }
      });
    });
    
    observer.observe(document.body, { attributes: true });
    
    return () => {
      observer.disconnect();
    };
  }, [showFunding]);
  
  // Add a small delay before showing the map to ensure proper rendering
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Monitor changes to the showFunding prop
  useEffect(() => {
    console.log("MapContainer: showFunding prop changed to", showFunding);
    
    // Add a data attribute to track the current funding state
    if (mapRef.current) {
      mapRef.current.setAttribute('data-funding-visible', String(showFunding));
    }
  }, [showFunding, mapRef]);
  
  return (
    <div className="relative w-full h-full">
      <div 
        ref={mapRef} 
        className="absolute inset-0 rounded-lg shadow-lg overflow-hidden touch-manipulation" 
        data-funding-visible={showFunding}
        style={{ 
          visibility: isVisible ? 'visible' : 'hidden',
          height: '100%',
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out'
        }}
      />
      
      {/* Add funding toggle button */}
      {isVisible && setShowFunding && (
        <ToggleFundingButton 
          showFunding={showFunding} 
          setShowFunding={setShowFunding}
        />
      )}
      
      {/* Add a subtle gradient overlay that matches our theme */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent to-jireh-primary/10 rounded-lg" />
      
      {/* Add a subtle grid overlay for futuristic effect */}
      <div className="absolute inset-0 pointer-events-none bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgwLDk2LDEyOCwwLjEpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')]  opacity-30" />
      
      {children}
    </div>
  );
};

export default MapContainer;
